/**
 * The one permission-label catalogue, read by BOTH apps (IMM-620.1).
 *
 * Every `WORKSPACE`/`ORG` permission an API key can carry, keyed by the literal
 * itself. Two readers, one source:
 *
 * - the customer app — `formatPermissions()` on Einstellungen → API-Schlüssel,
 *   and the issuance dialog's checkbox rows;
 * - the operator app — `usePermissionLabel()` behind the admin issue/edit
 *   catalogue.
 *
 * It lives at the TOP LEVEL rather than under `pages.*` because it belongs to
 * neither page: a second copy under a page namespace is the defect one refactor
 * later, since the next permission gets labelled in whichever namespace its
 * author happens to be looking at — which is exactly how the customer list came
 * to render `workspace:property:document-read` raw while the admin app had the
 * German for it (IMM-620).
 *
 * Both readers degrade to the raw literal on a miss — deliberate, and kept: an
 * unlabelled permission must never throw or print a message id in front of
 * anyone. The promise that the fallback never fires is the totality guard in
 * `apps/web-app/src/pages/settings/__tests__/api-key-permission-labels.test.ts`,
 * which derives the permission set from the contract's own catalogues. Note
 * `check:i18n-parity` cannot serve as that guard: it diffs each locale against
 * the union, so a permission missing from EVERY locale is perfect parity.
 *
 * Wording: where the customer catalogue and the operator catalogue named the
 * same literal differently, the CUSTOMER wording won — it distinguishes the
 * workspace-tier list read from the org-tier detail read („Deals lesen" vs
 * „Deal-Details lesen") where the operator wording collided on both.
 */
export const permissions = {
  'workspace:identity:organization-create': 'Standort erstellen',
  'workspace:identity:organization-coverage-write': 'Einzugsgebiet verwalten',
  'workspace:identity:organization-coverage-read': 'Einzugsgebiet lesen',
  'workspace:identity:workspace-role-assign': 'Workspace-Rollen zuweisen',
  'workspace:identity:organizations-read': 'Standorte anzeigen',
  'workspace:identity:app-handoff-exchange': 'Übergabe-Token einlösen',
  'workspace:identity:organization-read': 'Standort anzeigen',
  'workspace:identity:my-organizations-read': 'Eigene Standorte anzeigen',
  'workspace:identity:profile-read': 'Eigenes Profil anzeigen',
  'workspace:identity:profile-update': 'Eigenes Profil bearbeiten',
  'workspace:identity:preferences-read': 'Einstellungen anzeigen',
  'workspace:identity:preferences-update': 'Einstellungen bearbeiten',
  'workspace:identity:member-read': 'Mitglieder anzeigen',
  'workspace:identity:member-create': 'Mitglieder anlegen',
  'workspace:identity:member-update': 'Mitglieder bearbeiten',
  'workspace:identity:member-profile-read': 'Mitgliederprofile & Portraits lesen',
  'workspace:identity:member-profile-update': 'Mitgliederprofile bearbeiten',
  'workspace:identity:invitation-read': 'Einladungen anzeigen',
  'workspace:identity:invitation-resend': 'Einladungen erneut senden',
  'workspace:identity:invitation-revoke': 'Einladungen widerrufen',
  'workspace:identity:company-read': 'Firmendaten anzeigen',
  'workspace:identity:company-update': 'Firmendaten bearbeiten',
  'workspace:identity:branding-read': 'Branding anzeigen',
  'workspace:identity:branding-update': 'Branding bearbeiten',
  'workspace:identity:document-template-read': 'Vorlagen anzeigen',
  'workspace:identity:document-template-create': 'Vorlagen anlegen',
  'workspace:identity:document-template-update': 'Vorlagen bearbeiten',
  'workspace:identity:document-template-delete': 'Vorlagen löschen',
  'workspace:identity:email-template-read': 'E-Mail-Vorlagen anzeigen',
  'workspace:identity:email-template-create': 'E-Mail-Vorlagen anlegen',
  'workspace:identity:email-template-update': 'E-Mail-Vorlagen bearbeiten',
  'workspace:identity:email-template-delete': 'E-Mail-Vorlagen löschen',
  'workspace:relationship:contact-read': 'Kontakte anzeigen',
  'workspace:relationship:company-read': 'Firmen (CRM) anzeigen',
  'workspace:relationship:contact-reassign': 'Kontakte einem anderen Standort zuweisen',
  'workspace:property:property-read': 'Objekte lesen',
  'workspace:property:document-read': 'Objektdokumente anzeigen',
  'workspace:property:portal-connection-read': 'ImmoScout24-Verbindung ansehen',
  'workspace:property:property-reassign': 'Objekte einem anderen Standort zuweisen',
  'workspace:deal:deal-read': 'Deals lesen',
  'workspace:deal:prospect-read': 'Interessenten lesen',
  'workspace:deal:buyer-contract-template-read': 'Käufer-Maklervertrag-Vorlagen anzeigen',
  'workspace:deal:buyer-contract-template-create': 'Käufer-Maklervertrag-Vorlage anpassen',
  'workspace:deal:buyer-contract-template-update': 'Käufer-Maklervertrag-Vorlage bearbeiten',
  'workspace:deal:buyer-contract-template-delete': 'Käufer-Maklervertrag-Vorlage löschen',
  'workspace:deal:deal-reassign': 'Deals einem anderen Standort zuweisen',
  'workspace:lead:lead-read': 'Leads lesen',
  'workspace:lead:lead-reassign': 'Leads einem anderen Standort zuweisen',
  'workspace:assistant:conversation-use': 'Assistenten verwenden',
  'workspace:assistant:conversation-read': 'Assistenten-Verlauf anzeigen',
  'workspace:assistant:capability-gap-record': 'Nicht abgedeckte Anfragen erfassen',
  'workspace:assistant:skill-manage': 'Agent-Skills verwalten',
  'workspace:assistant:skill-read': 'Agent-Skill-Läufe ansehen',
  'workspace:communications:mailbox-connect': 'Postfach verbinden',
  'workspace:communications:mailbox-disconnect': 'Postfach trennen',
  'workspace:communications:mailbox-read': 'Postfächer anderer Mitglieder lesen',
  'workspace:communications:company-mailbox-read': 'Firmenpostfächer lesen',
  'workspace:communications:company-mailbox-manage': 'Firmenpostfächer verbinden und trennen',
  'workspace:communications:mailbox-thread-read': 'Postfach-Konversationen lesen',
  'workspace:communications:event-stream-read': 'Live-Aktualisierungen des Workspace empfangen',
  'workspace:communications:calendar-read': 'Eigenen Kalender lesen',
  'workspace:communications:calendar-write': 'Termine verwalten',
  'workspace:search:search-read': 'Workspace durchsuchen',
  'organization:identity:own-profile-read': 'Eigenes Profil anzeigen',
  'organization:identity:organization-read': 'Standort anzeigen',
  'organization:identity:organization-update': 'Standort bearbeiten',
  'organization:identity:member-read': 'Mitglieder anzeigen',
  'organization:identity:location-member-read': 'Standort-Mitglieder anzeigen',
  'organization:identity:member-create': 'Mitglieder anlegen',
  'organization:identity:member-update': 'Mitglieder bearbeiten',
  'organization:identity:member-role-assign': 'Mitgliederrollen zuweisen',
  'organization:identity:profile-read': 'Profil anzeigen',
  'organization:identity:profile-update': 'Profil bearbeiten',
  'organization:identity:member-profile-read': 'Mitgliederprofile anzeigen',
  'organization:identity:member-profile-update': 'Mitgliederprofile bearbeiten',
  'organization:identity:company-read': 'Firmendaten anzeigen',
  'organization:identity:company-update': 'Firmendaten bearbeiten',
  'organization:identity:partner-onboard': 'Partner-Onboarding durchführen',
  'organization:media:file-write': 'Dateien hochladen',
  'organization:media:file-read': 'Dateien anzeigen',
  'organization:media:file-delete': 'Dateien löschen',
  'organization:property:property-create': 'Objekte anlegen',
  'organization:property:property-read': 'Objekt-Details lesen',
  'organization:property:property-update': 'Objekte bearbeiten',
  'organization:property:property-reassign': 'Objekte zuweisen',
  'organization:property:property-delete': 'Objekte löschen',
  'organization:property:property-contact-write': 'Objektkontakte verwalten',
  'organization:property:document-read': 'Dokumente lesen',
  'organization:property:document-write': 'Objektdokumente verwalten',
  'organization:property:document-request': 'Dokumente anfordern',
  'organization:property:document-manage-any': 'Alle Objektdokumente verwalten',
  'organization:property:media-read': 'Bilder & Videos lesen',
  'organization:property:media-write': 'Objektmedien hochladen',
  'organization:property:media-manage-any': 'Alle Objektmedien verwalten',
  'organization:property:portal-listing-publish': 'Web-Exposés veröffentlichen',
  'organization:property:portal-listing-withdraw': 'Web-Exposés zurückziehen',
  'organization:property:portal-listing-browser-publish': 'Objekte per Browser-Erweiterung zu ImmoScout24 übertragen',
  'organization:deal:deal-create': 'Deals anlegen',
  'organization:deal:deal-read': 'Deal-Details lesen',
  'organization:deal:deal-phase-advance': 'Deals weiterschieben',
  'organization:deal:deal-phase-move-back': 'Deals zurückschieben',
  'organization:deal:deal-update': 'Deals bearbeiten',
  'organization:deal:prospect-create': 'Interessenten anlegen',
  'organization:deal:prospect-phase-advance': 'Interessenten weiterschieben',
  'organization:deal:prospect-phase-move-back': 'Interessenten zurückschieben',
  'organization:deal:prospect-update': 'Interessenten bearbeiten',
  'organization:deal:deal-delete': 'Deals löschen',
  'organization:deal:deal-assign': 'Deals zuweisen',
  'organization:deal:deal-oversight': 'Deal-Überblick (alle Deals des Standorts lesen)',
  'organization:deal:document-manage-any': 'Generierte Dokumente aller Mitglieder löschen',
  'organization:valuation:valuation-request-read': 'Bewertungen lesen',
  'organization:valuation:valuation-request-create': 'Bewertungsanfragen erstellen',
  'organization:valuation:valuation-request-release': 'Bewertungen freigeben',
  'organization:activity:activity-read': 'Aktivitäten lesen',
  'organization:activity:activity-update': 'Aktivitäten bearbeiten',
  'organization:activity:activity-write': 'Aktivitäten erstellen',
  'organization:activity:activity-oversight': 'Aktivitäts-Überblick (alle Aktivitäten des Standorts lesen)',
  'organization:activity:activity-delete': 'Aktivitäten anderer löschen',
  'organization:relationship:contact-create': 'Kontakte anlegen',
  'organization:relationship:contact-read': 'Kontakte anzeigen',
  'organization:relationship:contact-update': 'Kontakte bearbeiten',
  'organization:relationship:contact-delete': 'Kontakte löschen',
  'organization:relationship:contact-note-update': 'Kontaktnotizen bearbeiten',
  'organization:relationship:contact-note-delete': 'Kontaktnotizen löschen',
  'organization:relationship:contact-communication-update': 'Kontaktkommunikation bearbeiten',
  'organization:relationship:contact-communication-delete': 'Kontaktkommunikation löschen',
  'organization:relationship:company-create': 'Firmen anlegen',
  'organization:relationship:company-read': 'Firmen anzeigen',
  'organization:relationship:company-update': 'Firmen bearbeiten',
  'organization:relationship:company-archive': 'Firmen archivieren',
  'organization:relationship:company-unarchive': 'Firmen wiederherstellen',
  'organization:relationship:company-delete': 'Firmen löschen',
  'organization:relationship:company-contact-write': 'Firmenkontakte verwalten',
  'organization:lead:lead-create': 'Leads erstellen',
  'organization:lead:lead-read': 'Lead-Details lesen',
  'organization:lead:lead-update': 'Leads bearbeiten',
  'organization:lead:lead-assign': 'Leads zuweisen',
  'organization:lead:lead-partner-read': 'Lead-Partner anzeigen',
  'organization:lead:lead-partner-manage': 'Lead-Partner verwalten',
  'organization:communications:mailbox-send': 'E-Mails senden',
  'organization:communications:appointment-write': 'Termine anlegen',
} as const;
